import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { PetUpdateType } from 'angular-to-phaser';
import { Observable, of } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PetColorConfig, PetCreateRequest, PetDTO, PetResult, PetSpeciesConfig, QuizAnswer } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';


@Injectable({
    providedIn: 'root'
})
export class PetService extends AbstractService {

    private readonly activeUserPetsState: WritableSignal<PetDTO[]> = signal<PetDTO[]>([]);
    public readonly activeUserPetsSignal: Signal<PetDTO[]> = this.activeUserPetsState.asReadonly();
    public readonly activeUserPets: Observable<PetDTO[]> = toObservable(this.activeUserPetsState);

    constructor(private http: HttpClient, coreAppService: ApplicationService) {
        super(coreAppService.authService, coreAppService.featureFlagService);

        coreAppService.authService.authenticatedUser$.subscribe(a => {
            if (!(this.activeUserPetsState().length) && this.authService.isAuthenticated()) {
                this.initializeActiveUserPets();
            }
        });

    }

    public initializeActiveUserPets(): void {
        this.http.get<PetDTO[]>(environment.apiUrl + '/pet/all', this.httpOptionsAuthJson())
            .pipe(
                map(pets => pets.map(retPet => new PetDTO(retPet))))
            .subscribe(p => {
                if (p && p.length) {
                    this.activeUserPetsState.set(p);
                }
            });
    }

    // Notifies subscribers of an out-of-band mutation to a pet object the signal already holds
    // by reference (e.g. CustomizationService.equipPetBackground writing a pet's Background in
    // place) - wraps the same array in a fresh reference so the signal's default equality check
    // sees a change, without this service needing to know what changed.
    public refreshActiveUserPets(): void {
        this.activeUserPetsState.set([...this.activeUserPetsState()]);
    }

    public getPets(petIds: number[]): Observable<PetDTO[]> {
        if (!petIds || petIds.length === 0) { return of([]); }

        const foundActiveUserPets = this.activeUserPetsState()
            .filter(activeUserPet => petIds.findIndex(petId => petId === activeUserPet.Id) !== -1);

        petIds = petIds.filter(id => foundActiveUserPets.findIndex(p => p.Id === id) === -1); //remove ones we already have cached for the active user

        if (petIds.length > 0) {
            return this.http.get<PetDTO[]>(environment.apiUrl + `/pet?${petIds.map(id => `id=${id}`).join('&')}`, this.httpOptionsAuthJson())
                .pipe(
                    map(returnValue => returnValue
                        .map(retPet => new PetDTO(retPet))
                        .concat(foundActiveUserPets)));
        } else {
            return of(foundActiveUserPets);
        }
    }

    public getPetsForUser(username: string): Observable<PetDTO[]> {
        return this.http.get<PetDTO[]>(environment.apiUrl + `/pet/all/${username}`, this.httpOptionsAuthJson())
            .pipe(
                map(returnValue => returnValue
                    .map(retPet => new PetDTO(retPet))));
    }

    public getColors(): Observable<PetColorConfig[]> {
        return this.http.get(environment.apiUrl + '/pet/colors/',
            {
                withCredentials: true,
            }).pipe(
                retry(2),
                catchError(this.handleError),
                map((r: any) => r.Colors as PetColorConfig[])
            );
    }

    public getSpecies(): Observable<PetSpeciesConfig[]> {
        return this.http.get(environment.apiUrl + '/pet/species/',
            {
                withCredentials: true,
            }).pipe(
                retry(2),
                catchError(this.handleError),
                map((r: any) => r.Species as PetSpeciesConfig[])
            );
    }

    public createPet(pet: PetDTO, answers: QuizAnswer[]): Observable<PetDTO> {
        return this.http.post<PetDTO>(environment.apiUrl + '/pet/create/', new PetCreateRequest(pet, answers), this.httpOptionsAuthJson())
            .pipe(
                map(retPet => new PetDTO(retPet)),
                tap(petR => {
                    const pets = this.activeUserPetsState();
                    if (pets.findIndex(p => p.Id === petR.Id) === -1) {
                        this.activeUserPetsState.set([...pets, petR]);
                    }
                }));
    }

    public checkPetName(name: string): Observable<void> {
        return this.http.get<void>(environment.apiUrl + `/pet/valid/name/${name}`, this.httpOptionsAuthJson());
    }

    public updatePet(petChange: PetResult) {
        const pets = [...this.activeUserPetsState()];
        const changedPetIdx = pets.findIndex(p => p.Id === petChange.PetId);
        if (changedPetIdx === -1) {
            // Can happen if activeUserPets hasn't caught up with a pet acquired earlier in the
            // same session (see createPet()) - nothing cached to update.
            console.warn(`PetService.updatePet: pet ${petChange.PetId} not found in activeUserPets cache, skipping`);
            return;
        }
        const changedPet = pets[changedPetIdx];
        if (petChange.ChangedStat === PetUpdateType.Food) {
            changedPet.FoodLevel = petChange.FinalState;
        } else if (petChange.ChangedStat === PetUpdateType.Mood) {
            changedPet.UpdateMood(petChange.FinalState, petChange.FinalStateMoodImagePath);
        }
        pets[changedPetIdx] = changedPet;
        this.activeUserPetsState.set(pets);
    }
}
